export type CountdownPhase =
  | 'key9_in_progress'
  | 'key9_practice'
  | 'key9_unlocked'
  | 'key9_missed'
  | 'key9_practice_complete'
  | 'full_in_progress'
  | 'full_done';

export type NextPuzzleCountdownModel = {
  visible: boolean;
  label: string;
  ready: boolean;
  showLoadButton: boolean;
  loadLabel: 'Load New Puzzle';
};

export type CountdownUiState = {
  visible: boolean;
  compact: true;
  placement: 'result_card_footer';
};

export function getLocalDateString(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getNextLocalMidnight(date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1, 0, 0, 0, 0);
}

export function getTimeUntilNextPuzzle(now = new Date()): number {
  return getNextLocalMidnight(now).getTime() - now.getTime();
}

export function formatCountdown(ms: number): string {
  if (ms <= 0) return 'New puzzle is ready';
  if (ms < 60_000) return 'New puzzle soon';

  if (ms < 3_600_000) {
    const minutes = Math.floor(ms / 60_000);
    const seconds = Math.floor((ms % 60_000) / 1000);
    return `New puzzle in ${minutes}m ${seconds}s`;
  }

  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  return `New puzzle in ${hours}h ${minutes}m`;
}

export function getNextPuzzleMessage(now = new Date()): string {
  return formatCountdown(getTimeUntilNextPuzzle(now));
}

export function getNextPuzzleCountdownModel(
  now = new Date(),
  options: { fullDone?: boolean; timeUntilMs?: number } = {}
): NextPuzzleCountdownModel {
  const ms = options.timeUntilMs ?? getTimeUntilNextPuzzle(now);
  const ready = ms <= 0;
  const baseLabel = formatCountdown(ms);
  const label = options.fullDone && !ready
    ? `Come back tomorrow — ${lowercaseFirst(baseLabel)}`
    : baseLabel;

  return {
    visible: true,
    label,
    ready,
    showLoadButton: ready,
    loadLabel: 'Load New Puzzle'
  };
}

export function shouldShowNextPuzzleCountdown(phase: CountdownPhase): boolean {
  return phase === 'key9_unlocked'
    || phase === 'key9_missed'
    || phase === 'key9_practice_complete'
    || phase === 'full_done';
}

export function getCountdownUiState(phase: CountdownPhase): CountdownUiState {
  return {
    visible: shouldShowNextPuzzleCountdown(phase),
    compact: true,
    placement: 'result_card_footer'
  };
}

function lowercaseFirst(value: string): string {
  return value.length === 0 ? value : `${value[0].toLowerCase()}${value.slice(1)}`;
}
